import { API_URL } from '@constants';
import { useState } from 'react';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';

const ENDPOINT_URL = '/ord-orders/my-orders';

/**
 * Counts consumer orders that are not delivered or cancelled.
 */
export const useGetPendingMyOrdersCount = () => {
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [lastResponseTime, setLastResponseTime] = useState<Date | null>(null);

    const request = async (): Promise<number> => {
        setLoading(true);
        setError(null);
        let acc = 0;

        try {
            const paramsBase = new URLSearchParams({ ...getCommonParameters(), per_page: String(100) });
            let page = 1;
            let totalPages = 1;

            do {
                paramsBase.set('page', String(page));
                const response = await fetch(`${API_URL}${ENDPOINT_URL}?${paramsBase.toString()}`, {
                    method: 'GET',
                    headers: getHeaders(),
                });

                if (!response.ok) {
                    let err: ApiError;
                    try {
                        const json = await response.json();
                        err = new Error(json.message || '') as ApiError;
                        err.errors = json.errors;
                    } catch (e) {
                        err = new Error('') as ApiError;
                    }
                    err.statusCode = response.status;
                    throw err;
                }

                const json = await response.json();
                const data = (json?.data || []) as Array<{ status?: string }>;
                const pagination = json?.pagination || {};
                const pageCount = Array.isArray(data)
                    ? data.filter((o) => {
                          const s = (o?.status || '').toString().toLowerCase();
                          return s !== 'delivered' && s !== 'cancelled';
                      }).length
                    : 0;
                acc += pageCount;
                totalPages = Number(pagination?.total_pages || pagination?.totalPages || totalPages) || totalPages;
                page += 1;
            } while (page <= totalPages);

            return acc;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            handleCommonErrors(err);
            const fallback = typeof err?.statusCode === 'number' ? `Request failed (${err.statusCode})` : 'Request failed';
            setError(typeof err?.message === 'string' && err.message.trim() ? err.message : fallback);
            return 0;
        } finally {
            setLoading(false);
            setLastResponseTime(new Date());
        }
    };

    return { request, loading, error, lastResponseTime };
};
